<template>
  <div
    class="find-replace-dialog"
    v-if="isDialogVisible"
    :style="{top: `${position.top}px`, left: `${position.left}px`}"
    @mousedown="startDrag"
  >
    <div class="dialog-header">
      <span class="title">Tìm Kiếm</span>
      <button class="close-button" @click="closeDialog">✖</button>
    </div>
    <div class="dialog-body">
      <ms-input
        ref="searchInputExcel"
        v-model="findText"
        @keydown.enter="findNext"
        :max-length="255"
        placeholder="Nhập từ khóa cần tìm kiếm"
      />
      <br />
      <ms-checkbox label="Phân biệt chữ hoa chữ thường" v-model="searchFlags" />
    </div>
    <div class="dialog-footer">
      <ms-button text="Tiếp theo" @click="findNext()" />
      <ms-button
        text="Quay Lại"
        @click="findNext(false)"
        type="secondary-outline"
        :disabled="arraylastSearchPosition.length < 2"
      />

      <ms-button
        text="Đóng"
        type="secondary-outline"
        :shortkey-target="$ms.constant.Command.Cancel"
        has-border
        @click="closeDialog"
      />
    </div>
  </div>
</template>

<script>
import {ref, reactive, onMounted, inject, watch, nextTick} from 'vue';
import GC from '@grapecity/spread-sheets';
import {useToast} from 'vue-toast-notification';

export default {
  name: 'FindReplaceDialog',
  setup() {
    const spread = inject('spread');
    const isDialogVisible = inject('isDialogVisible');
    const findText = ref('');
    const position = reactive({top: 50, left: 50});
    const dragging = ref(false);
    const offset = reactive({x: 0, y: 0});
    const lastSearchPosition = reactive({row: 0, col: 0});
    const arraylastSearchPosition = ref([]);
    const searchCondition = new GC.Spread.Sheets.Search.SearchCondition();
    const indexSearch = ref(0);
    const searchFlags = ref(false);
    const searchInputExcel = ref(null);

    const showDialog = () => {
      nextTick(() => {
        if (searchInputExcel.value) {
          searchInputExcel.value.input.focus();
        }
      });
    };

    onMounted(() => {
      searchCondition.searchOrder = GC.Spread.Sheets.Search.SearchOrder.zOrder;
      searchCondition.searchTarget = GC.Spread.Sheets.Search.SearchFoundFlags.cellText;
      searchCondition.searchFlags = GC.Spread.Sheets.Search.SearchFlags.ignoreCase;
    });

    const closeDialog = () => {
      isDialogVisible.value = false;
    };

    const startDrag = event => {
      dragging.value = true;
      offset.x = event.clientX - position.left;
      offset.y = event.clientY - position.top;
      document.addEventListener('mousemove', onDrag);
      document.addEventListener('mouseup', stopDrag);
    };

    const onDrag = event => {
      if (dragging.value) {
        position.left = event.clientX - offset.x;
        position.top = event.clientY - offset.y;
      }
    };

    const stopDrag = () => {
      dragging.value = false;
      document.removeEventListener('mousemove', onDrag);
      document.removeEventListener('mouseup', stopDrag);
    };

    const findNext = (mode = true) => {
      if (spread.value) {
        try {
          let activeSheet = spread.value.getActiveSheet();
          let rowCounts = activeSheet.getRowCount();
          let colCounts = activeSheet.getColumnCount();
          searchCondition.startSheetIndex = spread.value.getActiveSheetIndex();
          searchCondition.endSheetIndex = spread.value.getActiveSheetIndex();
          searchCondition.searchString = findText.value.trim();
          if (searchFlags.value) {
            searchCondition.searchFlags = GC.Spread.Sheets.Search.SearchFlags.none;
          } else {
            searchCondition.searchFlags = GC.Spread.Sheets.Search.SearchFlags.ignoreCase;
          }
          if (!mode && arraylastSearchPosition.value.length > 1) {
            lastSearchPosition.row = arraylastSearchPosition.value[arraylastSearchPosition.value.length - 2].row;
            lastSearchPosition.col = arraylastSearchPosition.value[arraylastSearchPosition.value.length - 2].col;
          }
          searchCondition.columnStart = lastSearchPosition.col + 1;
          searchCondition.columnEnd = colCounts;
          searchCondition.rowStart = lastSearchPosition.row;
          searchCondition.rowEnd = rowCounts;
          var searchresult = spread.value.search(searchCondition);
          if (searchresult && searchresult.foundColumnIndex !== -1 && searchresult.foundRowIndex !== -1) {
            if (!mode) {
              indexSearch.value--;
              arraylastSearchPosition.value.pop();
            } else {
              arraylastSearchPosition.value.push({
                row: lastSearchPosition.row,
                col: lastSearchPosition.col,
              });
              indexSearch.value++;
            }
            lastSearchPosition.row = searchresult.foundRowIndex;
            lastSearchPosition.col = searchresult.foundColumnIndex;
            activeSheet.setActiveCell(searchresult.foundRowIndex, searchresult.foundColumnIndex);
            activeSheet.setSelection(searchresult.foundRowIndex, searchresult.foundColumnIndex, 1, 1);

            activeSheet.showCell(
              searchresult.foundRowIndex,
              searchresult.foundColumnIndex,
              GC.Spread.Sheets.VerticalPosition.center,
              GC.Spread.Sheets.HorizontalPosition.center,
            );
          } else {
            lastSearchPosition.row = 0;
            lastSearchPosition.col = -1;
            if (indexSearch.value == 0) {
              useToast().info('Không tìm thấy từ khóa', {position: 'top'});
            } else {
              useToast().info('Không tìm thấy thêm lần xuất hiện nào trong các tài liệu được chỉ định', {
                position: 'top',
              });

              indexSearch.value = 0;
            }
          }
        } finally {
        }
      }
    };

    /**
     * update lại status khi thay đổi từ khóa tìm kiếm
     */
    const changeTextFind = () => {
      indexSearch.value = 0;
      lastSearchPosition.row = 0;
      lastSearchPosition.col = 0;
      arraylastSearchPosition.value = [];
    };
    // Thêm context menu mới từ props
    watch(
      () => findText.value,
      (newValue, oldValue) => {
        if (newValue.trim() !== oldValue.trim()) {
          changeTextFind();
        }
      },
    );
    // Sử dụng watch để tự động gọi showDialog khi dialog mở
    watch(isDialogVisible, newVal => {
      if (newVal) {
        showDialog();
      }
    });

    return {
      findText,
      position,
      dragging,
      offset,
      lastSearchPosition,
      searchCondition,
      closeDialog,
      startDrag,
      onDrag,
      stopDrag,
      findNext,
      isDialogVisible,
      arraylastSearchPosition,
      changeTextFind,
      showDialog,
      searchInputExcel,
    };
  },
};
</script>

<style scoped lang="scss">
.find-replace-dialog {
  position: absolute;
  width: 450px;
  background-color: #f3f3f3;
  border: 1px solid #bfbfbf;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  font-family: Arial, sans-serif;
  color: #333;
  border-radius: 4px;

  .dialog-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px;
    font-size: 14px;
    font-weight: bold;
    background-color: #eaeaea;
    border-bottom: 1px solid #bfbfbf;
    cursor: move;
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
    .title {
      font-size: 16px;
      font-weight: bold;
    }
    .close-button {
      background: none;
      border: none;
      color: #333;
      font-size: 16px;
      font-weight: bold;
      cursor: pointer;
      padding: 0;
      margin: 0;
      line-height: 1;
      transition: color 0.2s;

      &:hover {
        color: #ff5c5c;
      }

      &:focus {
        outline: none;
      }
    }
  }

  .dialog-body {
    padding: 12px;

    label {
      font-size: 13px;
      color: #333;
    }

    .input-field {
      width: calc(100% - 16px);
      padding: 4px 8px;
      margin-top: 4px;
      font-size: 13px;
      border: 1px solid #ccc;
      border-radius: 3px;
      outline: none;

      &:focus {
        border-color: #4a90e2;
        box-shadow: 0 0 3px rgba(74, 144, 226, 0.5);
      }
    }
  }

  .dialog-footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 8px 12px;
    border-top: 1px solid #bfbfbf;
    background-color: #f0f0f0;
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;

    .dialog-button {
      padding: 6px 12px;
      font-size: 13px;
      font-weight: bold;
      color: #333;
      background-color: #ffffff;
      border: 1px solid #bfbfbf;
      border-radius: 3px;
      cursor: pointer;
      transition: background-color 0.2s ease;

      &:hover {
        background-color: #e6e6e6;
      }

      &:active {
        background-color: #d4d4d4;
      }
    }
  }
}
</style>